import React, { useState, useEffect } from 'react';
import { FaVideo, FaTrash, FaSave, FaExternalLinkAlt, FaFilm, FaBell, FaBellSlash, FaLink } from 'react-icons/fa';
import { getToken } from '../../utils/auth';
import { getApiBaseUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

const StreamManagement: React.FC = () => {
    const [streamUrl, setStreamUrl] = useState('');
    const [streamTitle, setStreamTitle] = useState('Live Stream');
    const [streamType, setStreamType] = useState('live');
    const [streamColor, setStreamColor] = useState('red');
    const [notify, setNotify] = useState(false); // Default false
    const [chatEnabled, setChatEnabled] = useState(true); // Default true
    const [thumbnail, setThumbnail] = useState<File | null>(null);
    const [streams, setStreams] = useState<any[]>([]); // Changed to array
    const [loading, setLoading] = useState(false);

    // For delete confirmation
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [streamToDelete, setStreamToDelete] = useState<string | null>(null);

    useEffect(() => {
        loadStreams();
    }, []);

    const loadStreams = async () => {
        try {
            const token = getToken();
            if (!token) return;

            const API_URL = getApiBaseUrl();
            const response = await fetch(`${API_URL}/stream/list`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            if (data.streams) {
                setStreams(data.streams);
            }
        } catch (error) {
            console.error('Load streams error:', error);
        }
    };

    const handleSetStream = async () => {
        if (!streamUrl.trim()) {
            toast.error('Vui lòng nhập URL stream');
            return;
        }

        try {
            setLoading(true);
            const token = getToken();
            if (!token) return;

            const formData = new FormData();
            formData.append('stream_url', streamUrl);
            formData.append('title', streamTitle);
            formData.append('type', streamType);
            formData.append('color', streamColor);
            formData.append('notify', notify.toString());
            formData.append('chat_enabled', chatEnabled.toString());

            if (thumbnail) {
                formData.append('thumbnail', thumbnail);
            }

            const API_URL = getApiBaseUrl();
            const response = await fetch(`${API_URL}/stream/set`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to set stream');
            }

            toast.success('Đã thêm stream thành công!');
            loadStreams(); // Reload list
            // Optional: Clear form or keep it
            // setStreamUrl(''); 
        } catch (error) {
            console.error('Set stream error:', error);
            toast.error('Không thể set stream');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleNotify = async (id: string, currentStatus: boolean) => {
        try {
            const token = getToken();
            if (!token) return;

            const API_URL = getApiBaseUrl();
            await fetch(`${API_URL}/stream/notify`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: id,
                    notify: !currentStatus
                })
            });

            toast.success(`Đã ${!currentStatus ? 'bật' : 'tắt'} thông báo!`);
            loadStreams();
        } catch (error) {
            console.error('Toggle notify error', error);
            toast.error('Lỗi cập nhật trạng thái thông báo');
        }
    };

    const confirmClearStream = (id: string) => {
        setStreamToDelete(id);
        setIsConfirmOpen(true);
    };

    const executeClearStream = async () => {
        if (!streamToDelete) return;

        try {
            setLoading(true);
            setIsConfirmOpen(false);
            const token = getToken();
            if (!token) return;

            const API_URL = getApiBaseUrl();
            const response = await fetch(`${API_URL}/stream/clear?id=${streamToDelete}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to clear stream');
            }

            toast.success('Đã xóa stream!');
            loadStreams();
            setStreamToDelete(null);
        } catch (error) {
            console.error('Clear stream error:', error);
            toast.error('Không thể xóa stream');
        } finally {
            setLoading(false);
        }
    };

    const copyStreamLink = (id: string) => {
        const link = `${window.location.origin}/live/${id}`;
        navigator.clipboard.writeText(link);
        toast.success('Đã sao chép link!');
    };

    return (
        <div className="h-full grid grid-cols-1 lg:grid-cols-12 gap-0 lg:divide-x divide-gray-200 dark:divide-gray-800 overflow-y-auto lg:overflow-hidden">
            {/* Left Column: Create Form */}
            <div className="lg:col-span-4 h-auto lg:h-full lg:overflow-y-auto p-4 sm:p-6 bg-white dark:bg-gray-900 delay-100 transition-colors custom-scrollbar border-b lg:border-b-0 border-gray-200 dark:border-gray-800">
                <div className="space-y-5 pt-2">
                    {/* Stream Title */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                            Tiêu đề nội dung <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={streamTitle}
                            onChange={(e) => setStreamTitle(e.target.value)}
                            placeholder="Ví dụ: Phim Mắt Biếc - Full HD"
                            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium"
                        />
                    </div>

                    {/* Stream URL */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                            Stream URL (m3u8/flv) <span className="text-red-500">*</span>
                        </label>
                        <div className="space-y-2">
                            <input
                                type="text"
                                value={streamUrl}
                                onChange={(e) => setStreamUrl(e.target.value)}
                                placeholder="https://example.com/hls/stream.m3u8"
                                className="w-full px-4 py-2.5 border border-blue-200 dark:border-gray-600 rounded-xl bg-blue-50/30 dark:bg-gray-800 text-blue-900 dark:text-blue-200 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-mono text-sm"
                            />
                            <p className="text-xs text-gray-500">
                                Hỗ trợ: <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">.m3u8</span> (HLS) hoặc <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">.flv</span>
                            </p>
                        </div>
                    </div>

                    {/* Type and Color */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                                Loại
                            </label>
                            <select
                                value={streamType}
                                onChange={(e) => setStreamType(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500"
                            >
                                <option value="live">📡 Live Stream</option>
                                <option value="movie">🎬 Phim (Movie)</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                                Màu Sắc
                            </label>
                            <select
                                value={streamColor}
                                onChange={(e) => setStreamColor(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500"
                            >
                                <option value="red">🔴 Đỏ</option>
                                <option value="blue">🔵 Xanh Dương</option>
                                <option value="green">🟢 Xanh Lá</option>
                                <option value="purple">🟣 Tím</option>
                                <option value="orange">🟠 Cam</option>
                            </select>
                        </div>
                    </div>

                    {/* Thumbnail */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                            Poster / Thumbnail
                        </label>
                        <div className="flex items-center justify-center w-full">
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    {thumbnail ? (
                                        <div className="text-center">
                                            <p className="text-sm font-medium text-green-600 truncate max-w-[200px]">{thumbnail.name}</p>
                                            <p className="text-xs text-gray-500">Click để thay đổi</p>
                                        </div>
                                    ) : (
                                        <>
                                            <FaVideo className="w-8 h-8 text-gray-400 mb-2" />
                                            <p className="text-sm text-gray-500 dark:text-gray-400"><span className="font-semibold">Tải ảnh lên</span> hoặc kéo thả</p>
                                        </>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*,.webp"
                                    onChange={(e) => setThumbnail(e.target.files ? e.target.files[0] : null)}
                                />
                            </label>
                        </div>
                    </div>

                    {/* Options Row */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl space-y-3">
                        {/* Notify Checkbox */}
                        <div className="flex items-center group cursor-pointer" onClick={() => setNotify(!notify)}>
                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${notify ? 'bg-red-500 border-red-500' : 'bg-white border-gray-400 dark:bg-gray-700'}`}>
                                {notify && <span className="text-white text-xs">✓</span>}
                            </div>
                            <label className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer select-none">
                                Gửi thông báo Banner (Toàn Server)
                            </label>
                        </div>

                        {/* Chat Enabled Checkbox */}
                        <div className="flex items-center group cursor-pointer" onClick={() => setChatEnabled(!chatEnabled)}>
                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${chatEnabled ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400 dark:bg-gray-700'}`}>
                                {chatEnabled && <span className="text-white text-xs">✓</span>}
                            </div>
                            <label className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer select-none">
                                Bật tính năng Chat
                            </label>
                        </div>
                    </div>

                    <button
                        onClick={handleSetStream}
                        disabled={loading || !streamUrl.trim()}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl shadow-lg hover:shadow-red-500/30 transition-all transform hover:-translate-y-0.5"
                    >
                        {loading ? <span className="animate-spin">⏳</span> : <FaSave />}
                        <span>{loading ? 'Đang Xử Lý...' : 'Đăng Nội Dung Mới'}</span>
                    </button>

                    <p className="text-center text-xs text-gray-400 mt-2">
                        * Nội dung sẽ xuất hiện ngay lập tức trên trang Live.
                    </p>
                </div>
            </div>

            {/* Right Column: List */}
            <div className="lg:col-span-8 h-auto lg:h-full lg:overflow-y-auto p-4 sm:p-6 custom-scrollbar bg-gray-50 dark:bg-gray-900">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                            Danh Sách Đang Phát
                        </h3>
                        <p className="text-sm text-gray-500">
                            Quản lý độ ưu tiên và trạng thái của các luồng phát.
                        </p>
                    </div>
                    <span className="inline-flex items-center whitespace-nowrap bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full text-xs font-bold border border-blue-200 dark:border-blue-800 shadow-sm">
                        Total: {streams.length}
                    </span>
                </div>

                {streams.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                        <FaFilm className="text-6xl mb-4 opacity-20" />
                        <p>Chưa có nội dung nào.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {streams.map((stream) => (
                            <div key={stream.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all overflow-hidden group flex h-36">
                                {/* Thumbnail - Full Height */}
                                <div className="w-28 bg-gray-200 dark:bg-gray-800 relative shrink-0 h-full">
                                    {stream.thumbnail ? (() => {
                                        const filename = stream.thumbnail.split('/').pop();
                                        const imageUrl = stream.thumbnail.startsWith('http')
                                            ? stream.thumbnail
                                            : `${getApiBaseUrl()}/stream/image?file=${filename}`;
                                        return (
                                            <img
                                                src={imageUrl}
                                                className="w-full h-full object-cover"
                                                alt=""
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    e.currentTarget.parentElement?.classList.add('bg-gray-700');
                                                }}
                                            />
                                        );
                                    })() : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                            <FaVideo />
                                        </div>
                                    )}
                                </div>

                                {/* Content & Actions */}
                                <div className="flex-1 flex flex-col justify-between min-w-0">
                                    <div className="p-3 flex flex-col gap-1">
                                        <div className="flex justify-between items-start gap-2">
                                            <div className="min-w-0 flex-1">
                                                <h4 className="font-bold text-gray-900 dark:text-gray-100 text-sm line-clamp-1 mb-0.5" title={stream.title}>
                                                    {stream.title}
                                                </h4>
                                                <p className="text-xs text-gray-500 font-mono truncate bg-gray-100 dark:bg-gray-800/50 px-1.5 py-0.5 rounded w-fit max-w-full">
                                                    ID: {stream.id}
                                                </p>
                                            </div>
                                            <div className={`shrink-0 px-2 py-1 text-[10px] font-bold rounded text-white ${stream.type === 'live' ? 'bg-red-600' : 'bg-blue-600'}`}>
                                                {stream.type === 'live' ? 'TRỰC TIẾP' : 'PHIM'}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {stream.notify && (
                                                <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-bold border border-yellow-200">
                                                    🔔 NOTIFY
                                                </span>
                                            )}
                                            {!stream.chatEnabled && (
                                                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-bold border border-gray-200">
                                                    🔇 NO CHAT
                                                </span>
                                            )}
                                            {stream.is_active !== false && (
                                                <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold flex items-center gap-1">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                                                    ACTIVE
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Footer Actions */}
                                    <div className="flex items-center justify-between px-3 pb-2 pt-2 border-t border-gray-100 dark:border-gray-800/50 mt-auto">
                                        <div className="flex gap-1 items-center">
                                            <button
                                                onClick={() => handleToggleNotify(stream.id, stream.notify)}
                                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${stream.notify ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400'}`}
                                                title={stream.notify ? 'Tắt thông báo' : 'Bật thông báo'}
                                            >
                                                {stream.notify ? <FaBell size={14} /> : <FaBellSlash size={14} />}
                                            </button>
                                            <button
                                                onClick={() => copyStreamLink(stream.id)}
                                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-500 transition-all"
                                                title="Copy Link"
                                            >
                                                <FaLink size={14} />
                                            </button>
                                            <a
                                                href={`/live/${stream.id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-all"
                                                title="Xem thử"
                                            >
                                                <FaExternalLinkAlt size={14} />
                                            </a>
                                        </div>

                                        <button
                                            onClick={() => confirmClearStream(stream.id)}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded text-xs font-bold transition-colors flex items-center gap-1"
                                        >
                                            <FaTrash /> Xoá
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={executeClearStream}
                title="Xóa Stream"
                message="Bạn có chắc chắn muốn xóa stream này không?"
                type="danger"
                confirmText="Xóa"
            />
        </div>
    );
};

export default StreamManagement;
